/**
 * @file AllocateCreditNoteModal.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-04-11
 * @modified 2026-10-15
 *
 * 1.2.0 (2026-10-15, Cam): a customer credit note made here with stock-tracked
 * lines is asked, before it is allocated, what each line does to stock —
 * goods returned or price correction, both always offered — against the
 * invoice(s) being allocated to (CreditStockDialog mode "allocate", rows from
 * GET /api/invoices/:id/credit-stock/allocation; each starts on the kind
 * stored at save, else what the quantity suggests). The allocation carries
 * the answer (credit_stock_confirmed, credit_stock, credit_stock_reason); a
 * kind that differs changes the credit first (server side). If that change
 * is refused nothing is allocated and the reason is shown, with "Allocate
 * keeping the saved effect" or Back to the question. Every way into this
 * window asks: a credit's Allocate…, an invoice's Allocate Credit, the credit
 * allocation review and "Allocate now" after Add credit. Till / Xero-made
 * credits and overpayments are not asked.
 *
 * 1.1.0 (2026-10-08): `preselectInvoiceId` / `preselectAmount` — "Allocate
 * now" after Add Credit opens this with the invoice the credit was raised for
 * already filled in, at min(remaining credit, its balance[, amount]). Never
 * more than either: the same cap the input has always had.
 */

import React, { useState, useEffect } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import Input from './Input';
import AlertDialog from './AlertDialog';
import LoadingSpinner from './LoadingSpinner';
import { useSlideOutButton } from './SlideOutButton';
import CreditStockDialog from './CreditStockDialog';
import ConfirmDialog from './ConfirmDialog';
import { allocationChoices, storedEffect } from '../utils/creditStockEffect';

function AllocateCreditNoteModal({ isOpen, onClose, creditNote, onSuccess, preselectInvoiceId = null, preselectAmount = null }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [remainingCredit, setRemainingCredit] = useState(0);
  const [existingAllocations, setExistingAllocations] = useState([]);
  const [eligibleInvoices, setEligibleInvoices] = useState([]);
  const [allocations, setAllocations] = useState({}); // { invoiceId: amount }
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });
  // The stock question before allocating ({ plan, choices, reason, payload }),
  // and a refused change of the credit's stock effect ({ message, ask }).
  const [stockAsk, setStockAsk] = useState(null);
  const [stockRefused, setStockRefused] = useState(null);

  useSlideOutButton([isOpen, loading, saving, allocations]);

  useEffect(() => {
    if (isOpen && creditNote) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, creditNote?.id]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Fetch allocation details (remaining credit, existing allocations)
      const allocRes = await fetch(`${API_URL}/api/invoices/${creditNote.id}/allocations`, {
        credentials: 'include'
      });
      const allocData = await allocRes.json();

      if (allocData.success) {
        setRemainingCredit(parseFloat(allocData.remainingCredit) || 0);
        setExistingAllocations(allocData.allocations || []);
      }

      // Fetch outstanding invoices for same contact
      const invRes = await fetch(
        `${API_URL}/api/invoices/search?status=AUTHORISED&type=invoice&accountId=${creditNote.account_id}&limit=500`,
        { credentials: 'include' }
      );
      const invData = await invRes.json();

      // Filter to invoices with outstanding balance
      const eligible = (invData.invoices || []).filter(inv =>
        parseFloat(inv.balance) > 0.01 && inv.xero_invoice_id
      );
      setEligibleInvoices(eligible);

      // "Allocate now" after Add Credit: the invoice it was raised for, filled in.
      const remaining = allocData.success ? (parseFloat(allocData.remainingCredit) || 0) : 0;
      const target = preselectInvoiceId != null && eligible.find(inv => Number(inv.id) === Number(preselectInvoiceId));
      if (target && remaining > 0) {
        const cap = Math.min(parseFloat(target.balance) || 0, remaining);
        const wanted = parseFloat(preselectAmount) > 0 ? Math.min(parseFloat(preselectAmount), cap) : cap;
        const amount = Math.round(wanted * 100) / 100;
        if (amount > 0) setAllocations({ [target.id]: amount });
      }
    } catch (error) {
      setAlertDialog({ isOpen: true, message: 'Failed to load data: ' + error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAmountChange = (invoiceId, value) => {
    const amount = parseFloat(value) || 0;
    setAllocations(prev => {
      if (amount <= 0) {
        const next = { ...prev };
        delete next[invoiceId];
        return next;
      }
      return { ...prev, [invoiceId]: amount };
    });
  };

  const totalAllocating = Object.values(allocations).reduce((sum, a) => sum + a, 0);
  const overAllocated = totalAllocating > remainingCredit + 0.01;

  const handleSubmit = async () => {
    if (Object.keys(allocations).length === 0) {
      setAlertDialog({ isOpen: true, message: 'Please enter at least one allocation amount', type: 'warning' });
      return;
    }
    if (overAllocated) {
      setAlertDialog({ isOpen: true, message: 'Total allocations exceed remaining credit', type: 'warning' });
      return;
    }

    const payload = Object.entries(allocations).map(([invoiceId, amount]) => ({
      invoiceId: parseInt(invoiceId),
      amount
    }));

    // A credit with stock-tracked lines: ask first (nothing to ask → straight on).
    setSaving(true);
    try {
      const ids = payload.map(a => a.invoiceId).join(',');
      const res = await fetch(`${API_URL}/api/invoices/${creditNote.id}/credit-stock/allocation?invoiceIds=${ids}`, {
        credentials: 'include'
      });
      const plan = await res.json();
      if (!res.ok || !plan.success) throw new Error(plan.error || 'the check failed');
      if (plan.applies && plan.ready !== false && (plan.rows || []).length) {
        setStockAsk({ plan, choices: {}, reason: plan.change?.default_reason || '', payload });
        setSaving(false);
        return;
      }
    } catch (error) {
      setAlertDialog({ isOpen: true, type: 'error', message: `Could not check this credit note's stock lines (${error.message}). Nothing was allocated — try again.` });
      setSaving(false);
      return;
    }
    await sendAllocation(payload, null);
  };

  // `stock`: { kinds, reason, ask } — the answer to the stock question (null when none was asked).
  const sendAllocation = async (payload, stock) => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/invoices/${creditNote.id}/allocate-credit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          allocations: payload,
          ...(stock ? {
            credit_stock_confirmed: true,
            credit_stock: stock.kinds,
            ...(stock.reason ? { credit_stock_reason: stock.reason } : {})
          } : {})
        })
      });

      const result = await response.json();
      if (response.ok && result.success) {
        if (onSuccess) onSuccess();
        onClose();
        return;
      }
      // Asked by the server (the credit changed since the check): ask now.
      if (result.code === 'CREDIT_STOCK_CONFIRM_REQUIRED' && result.details?.plan) {
        const plan = result.details.plan;
        setStockAsk({ plan, choices: stock?.ask?.choices || {}, reason: stock?.reason || plan.change?.default_reason || '', payload });
        return;
      }
      // The credit's stock effect could not be changed: nothing was allocated.
      if (result.code === 'CREDIT_STOCK_CHANGE_REFUSED') {
        const plan = result.details?.plan || stock?.ask?.plan;
        setStockRefused({ message: result.error, ask: { ...(stock?.ask || {}), plan, payload } });
        return;
      }
      throw new Error(result.error || 'Failed to allocate credit');
    } catch (error) {
      setAlertDialog({ isOpen: true, message: error.message, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const confirmStock = () => {
    const ask = stockAsk;
    const { kinds, changes } = allocationChoices(ask.plan.rows, ask.choices);
    setStockAsk(null);
    // The reason is the credit's edit's: sent only when a kind changes.
    sendAllocation(ask.payload, { kinds, reason: changes.length ? ask.reason : null, ask });
  };

  // Allocate as the credit stands: each line keeps what it does now.
  const allocateKeepingStored = () => {
    const ask = stockRefused.ask;
    setStockRefused(null);
    const kinds = (ask.plan?.rows || []).map(r => ({ line_id: r.line_id, credit_kind: storedEffect(r.stored) }));
    sendAllocation(ask.payload, { kinds, reason: null, ask });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
  };

  const footer = (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
      <div style={{ fontSize: 'var(--font-sm)', color: 'var(--text-secondary)' }}>
        Allocating: <strong>{formatCurrency(totalAllocating)}</strong> of {formatCurrency(remainingCredit)} remaining
        {overAllocated && <span style={{ color: 'var(--color-error)', marginLeft: '8px' }}>Exceeds remaining credit</span>}
      </div>
      <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
        <button data-read-safe className="btn-round btn-danger" onClick={onClose} disabled={saving}>
          <div className="icon-wrapper">
            <span className="material-symbols-rounded">close</span>
          </div>
          <span className="btn-label">Cancel</span>
        </button>
        <button
          className="btn-slideout btn-slideout-primary"
          onClick={handleSubmit}
          disabled={saving || overAllocated || Object.keys(allocations).length === 0}
        >
          <div className="icon-wrapper">
            <span className={`material-symbols-rounded ${saving ? 'spinning' : ''}`}>
              {saving ? 'sync' : 'credit_score'}
            </span>
          </div>
          <span className="btn-label">{saving ? 'Allocating...' : 'Allocate Credit'}</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={`Allocate ${creditNote?.invoice_no || 'Credit Note'}`}
        size="lg"
        footer={footer}
      >
        {loading ? (
          <LoadingSpinner />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-lg)', padding: 'var(--spacing-lg) var(--spacing-xl) var(--spacing-xl)' }}>
            {/* Credit note summary */}
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--spacing-md)',
              background: 'var(--bg-tertiary)', padding: 'var(--spacing-md)', borderRadius: 'var(--radius-lg)'
            }}>
              <div>
                <div style={{ fontSize: 'var(--font-xs)', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Credit Note</div>
                <div style={{ fontWeight: 600 }}>{creditNote?.invoice_no}</div>
              </div>
              <div>
                <div style={{ fontSize: 'var(--font-xs)', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Contact</div>
                <div>{creditNote?.company_name}</div>
              </div>
              <div>
                <div style={{ fontSize: 'var(--font-xs)', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Total</div>
                <div style={{ fontWeight: 600 }}>{formatCurrency(Math.abs(parseFloat(creditNote?.total || 0)))}</div>
              </div>
              <div>
                <div style={{ fontSize: 'var(--font-xs)', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Remaining</div>
                <div style={{ fontWeight: 600, color: remainingCredit > 0 ? 'var(--color-success)' : 'var(--text-muted)' }}>
                  {formatCurrency(remainingCredit)}
                </div>
              </div>
            </div>

            {/* Existing allocations */}
            {existingAllocations.length > 0 && (
              <div>
                <h4 style={{ margin: '0 0 var(--spacing-sm)', fontSize: 'var(--font-sm)', color: 'var(--text-secondary)' }}>
                  Existing Allocations
                </h4>
                <table className="data-table" style={{ fontSize: 'var(--font-sm)' }}>
                  <thead>
                    <tr>
                      <th>Invoice</th>
                      <th>Contact</th>
                      <th>Date</th>
                      <th className="text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {existingAllocations.map((alloc, i) => (
                      <tr key={i}>
                        <td>{alloc.target_invoice_no}</td>
                        <td>{alloc.company_name}</td>
                        <td>{alloc.date ? new Date(alloc.date).toLocaleDateString('en-GB') : '-'}</td>
                        <td className="text-right">{formatCurrency(alloc.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Eligible invoices to allocate against */}
            {remainingCredit > 0 ? (
              <div>
                <h4 style={{ margin: '0 0 var(--spacing-sm)', fontSize: 'var(--font-sm)', color: 'var(--text-secondary)' }}>
                  Outstanding Invoices
                </h4>
                {eligibleInvoices.length === 0 ? (
                  <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-sm)' }}>
                    No outstanding invoices found for this contact.
                  </p>
                ) : (
                  <table className="data-table" style={{ fontSize: 'var(--font-sm)' }}>
                    <thead>
                      <tr>
                        <th>Invoice</th>
                        <th>Date</th>
                        <th>Reference</th>
                        <th className="text-right">Total</th>
                        <th className="text-right">Balance</th>
                        <th className="text-right" style={{ width: '120px' }}>Allocate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eligibleInvoices.map(inv => (
                        <tr key={inv.id}>
                          <td style={{ fontWeight: 500 }}>{inv.invoice_no}</td>
                          <td>{inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString('en-GB') : '-'}</td>
                          <td>{inv.reference || '-'}</td>
                          <td className="text-right">{formatCurrency(inv.total)}</td>
                          <td className="text-right">{formatCurrency(inv.balance)}</td>
                          <td className="text-right">
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              max={Math.min(parseFloat(inv.balance), remainingCredit)}
                              value={allocations[inv.id] || ''}
                              onChange={(e) => handleAmountChange(inv.id, e.target.value)}
                              placeholder="0.00"
                              style={{ width: '100px', textAlign: 'right' }}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ) : (
              <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-sm)' }}>
                This credit note has been fully allocated.
              </p>
            )}
          </div>
        )}
      </Modal>

      <CreditStockDialog
        mode="allocate"
        isOpen={!!stockAsk}
        plan={stockAsk?.plan}
        choices={stockAsk?.choices || {}}
        reason={stockAsk?.reason || ''}
        onReasonChange={(reason) => setStockAsk(prev => (prev ? { ...prev, reason } : prev))}
        onChoose={(lineId, kind) => setStockAsk(prev => (prev ? { ...prev, choices: { ...prev.choices, [lineId]: kind } } : prev))}
        onConfirm={confirmStock}
        onCancel={() => setStockAsk(null)}
      />

      <ConfirmDialog
        isOpen={!!stockRefused}
        type="error"
        title="Stock effect not changed — nothing allocated"
        message={stockRefused?.message || ''}
        confirmText="Allocate keeping the saved effect"
        confirmIcon="credit_score"
        cancelText="Back to the stock question"
        onConfirm={allocateKeepingStored}
        onCancel={() => {
          const ask = stockRefused?.ask;
          setStockRefused(null);
          if (ask?.plan) setStockAsk({ plan: ask.plan, choices: ask.choices || {}, reason: ask.reason || ask.plan.change?.default_reason || '', payload: ask.payload });
        }}
      />

      {alertDialog.isOpen && (
        <AlertDialog
          isOpen={true}
          message={alertDialog.message}
          type={alertDialog.type}
          onClose={() => setAlertDialog({ isOpen: false, message: '', type: 'info' })}
        />
      )}
    </>
  );
}

export default AllocateCreditNoteModal;
